
import { supabase } from '@/integrations/supabase/client';
import { Prospect } from '@/types/pipeline';
import { Database } from '@/integrations/supabase';

type CrmProspect = Database['public']['Tables']['crm_prospects']['Row'];

/**
 * Update a prospect's stage
 * @param prospectId Prospect ID
 * @param newStage New stage
 */
export const updateProspectStage = async (
  prospectId: string, 
  newStage: string
): Promise<CrmProspect> => {
  const { data, error } = await supabase
    .from('crm_prospects')
    .update({ 
      stage: newStage,
      updated_at: new Date().toISOString()
    })
    .eq('id', prospectId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update prospect stage: ${error.message}`);
  }

  return data;
};

/**
 * Update prospect details
 * @param prospectId Prospect ID
 * @param updates Prospect updates
 */
export const updateProspectDetails = async (
  prospectId: string,
  updates: Partial<CrmProspect>
): Promise<CrmProspect> => {
  const { data, error } = await supabase
    .from('crm_prospects')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', prospectId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update prospect: ${error.message}`);
  }

  return data;
};

/**
 * Delete a prospect
 * @param prospectId Prospect ID
 */
export const deleteProspect = async (prospectId: string): Promise<void> => {
  const { error } = await supabase
    .from('crm_prospects')
    .delete()
    .eq('id', prospectId);

  if (error) {
    throw new Error(`Failed to delete prospect: ${error.message}`);
  }
};

/**
 * Create a new prospect
 * @param prospectData Prospect data
 */
export const createProspect = async (prospectData: Omit<CrmProspect, 'id' | 'created_at' | 'updated_at' | 'value' | 'expected_close_date'> & { value?: number, expected_close_date?: string }): Promise<CrmProspect> => {
  const { data, error } = await supabase
    .from('crm_prospects')
    .insert([prospectData])
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create prospect: ${error.message}`);
  }

  return data;
};
